import React, { useState, useEffect } from 'react';
import { 
  PartnershipRevenueSharing, 
  RevenueSharingTransaction, 
  RevenueSharingSummary,
  revenueSharingAPI 
} from '../../services/revenueSharingAPI';
import { toast } from 'react-hot-toast';

const RevenueSharingManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'configurations' | 'transactions' | 'summary'>('configurations');
  const [configurations, setConfigurations] = useState<PartnershipRevenueSharing[]>([]);
  const [transactions, setTransactions] = useState<RevenueSharingTransaction[]>([]);
  const [summaries, setSummaries] = useState<RevenueSharingSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PartnershipRevenueSharing | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);

  // Load data based on active tab
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'configurations':
          const configs = await revenueSharingAPI.getAllConfigs();
          setConfigurations(configs);
          break;
        case 'transactions':
          const trans = await revenueSharingAPI.getTransactions();
          setTransactions(trans);
          break;
        case 'summary':
          const sums = await revenueSharingAPI.getSummary();
          setSummaries(sums);
          break;
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (config: PartnershipRevenueSharing) => {
    try {
      if (config.id) {
        await revenueSharingAPI.updateConfig(config.country_code, config);
        toast.success('Configuration updated successfully');
      } else {
        await revenueSharingAPI.createConfig(config);
        toast.success('Configuration created successfully');
      }
      setShowConfigModal(false);
      setEditingConfig(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleDeleteConfig = async (countryCode: string) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        await revenueSharingAPI.deleteConfig(countryCode);
        toast.success('Configuration deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete configuration');
      }
    }
  };

  const handleBatchUpdateStatus = async (status: string) => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select transactions to update');
      return;
    }

    try {
      await revenueSharingAPI.batchUpdateTransactionStatus(selectedTransactions, status);
      toast.success('Transactions updated successfully');
      setSelectedTransactions([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update transactions');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Revenue Sharing Management</h1>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('configurations')}
              className={`${
                activeTab === 'configurations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Configurations
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`${
                activeTab === 'transactions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`${
                activeTab === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Summary
            </button>
          </nav>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Configurations Tab */}
          {activeTab === 'configurations' && (
            <div>
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => {
                    setEditingConfig(null);
                    setShowConfigModal(true);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Configuration
                </button>
              </div>

              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue Share %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Partner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {configurations.map((config) => (
                      <tr key={config.country_code}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {config.country_name} ({config.country_code})
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {config.revenue_share_percentage}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{config.partner_name || '-'}</div>
                          <div className="text-sm text-gray-500">{config.partner_email || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            config.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {config.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingConfig(config);
                              setShowConfigModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteConfig(config.country_code)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div>
              <div className="mb-4 flex justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBatchUpdateStatus('processed')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                    disabled={selectedTransactions.length === 0}
                  >
                    Mark as Processed
                  </button>
                  <button
                    onClick={() => handleBatchUpdateStatus('paid')}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    disabled={selectedTransactions.length === 0}
                  >
                    Mark as Paid
                  </button>
                </div>
              </div>

              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTransactions(transactions.map(t => t.id!));
                            } else {
                              setSelectedTransactions([]);
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gross Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Share Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.includes(transaction.id!)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTransactions([...selectedTransactions, transaction.id!]);
                              } else {
                                setSelectedTransactions(selectedTransactions.filter(id => id !== transaction.id));
                              }
                            }}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(transaction.created_at!).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.country_code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {transaction.transaction_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${transaction.gross_amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${transaction.share_amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            transaction.status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : transaction.status === 'processed'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="grid grid-cols-1 gap-6">
              {summaries.map((summary) => (
                <div key={summary.country_code} className="bg-white shadow-md rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {summary.country_name} ({summary.country_code})
                      </h3>
                      <p className="text-sm text-gray-500">Partner: {summary.partner_name || 'Not assigned'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Garages</p>
                      <p className="text-2xl font-semibold text-gray-900">{summary.total_garages}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Transactions</p>
                      <p className="text-2xl font-semibold text-gray-900">{summary.total_transactions}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Gross Revenue</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        ${summary.total_gross_amount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Revenue Share</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        ${summary.total_share_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Pending Amount</p>
                        <p className="text-lg font-semibold text-yellow-600">
                          ${summary.pending_share_amount.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Paid Amount</p>
                        <p className="text-lg font-semibold text-green-600">
                          ${summary.paid_share_amount.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Transaction</p>
                        <p className="text-sm text-gray-900">
                          {summary.last_transaction_date 
                            ? new Date(summary.last_transaction_date).toLocaleDateString()
                            : 'No transactions'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <ConfigurationModal
          config={editingConfig}
          onSave={handleSaveConfig}
          onClose={() => {
            setShowConfigModal(false);
            setEditingConfig(null);
          }}
        />
      )}
    </div>
  );
};

// Configuration Modal Component
interface ConfigurationModalProps {
  config: PartnershipRevenueSharing | null;
  onSave: (config: PartnershipRevenueSharing) => void;
  onClose: () => void;
}

const ConfigurationModal: React.FC<ConfigurationModalProps> = ({ config, onSave, onClose }) => {
  const [formData, setFormData] = useState<PartnershipRevenueSharing>({
    country_code: '',
    country_name: '',
    revenue_share_percentage: 0,
    partner_name: '',
    partner_email: '',
    partner_phone: '',
    is_active: true,
    notes: '',
    bank_account_details: {
      bank_name: '',
      account_name: '',
      account_number: '',
      iban: '',
      swift_code: '',
      branch_name: '',
      branch_code: '',
      currency: ''
    },
    ...config
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {config ? 'Edit' : 'Add'} Revenue Sharing Configuration
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country Code *
                </label>
                <input
                  type="text"
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  maxLength={2}
                  required
                  disabled={!!config}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country Name *
                </label>
                <input
                  type="text"
                  value={formData.country_name}
                  onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Revenue Share Percentage *
              </label>
              <input
                type="number"
                value={formData.revenue_share_percentage}
                onChange={(e) => setFormData({ ...formData, revenue_share_percentage: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                max="100"
                step="0.01"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partner Name
                </label>
                <input
                  type="text"
                  value={formData.partner_name || ''}
                  onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partner Email
                </label>
                <input
                  type="email"
                  value={formData.partner_email || ''}
                  onChange={(e) => setFormData({ ...formData, partner_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partner Phone
              </label>
              <input
                type="tel"
                value={formData.partner_phone || ''}
                onChange={(e) => setFormData({ ...formData, partner_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-medium mb-2">Bank Account Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_details?.bank_name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_account_details: {
                        ...formData.bank_account_details,
                        bank_name: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_details?.account_name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_account_details: {
                        ...formData.bank_account_details,
                        account_name: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_details?.iban || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_account_details: {
                        ...formData.bank_account_details,
                        iban: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SWIFT Code
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_details?.swift_code || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_account_details: {
                        ...formData.bank_account_details,
                        swift_code: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RevenueSharingManagement;